import {
  CHANGE_REQUEST_STATE_LABELS,
  LAUNCH_APPROVER_ROLE_LABELS,
  LAUNCH_ITEM_STATUS_LABELS,
  LEVEL_B_CHANGE_KIND_LABELS,
  PROJECT_ACTIVATION_STATE_LABELS,
  type ChangeRequest,
  type LaunchApproval,
  type LaunchItem,
  type LaunchItemStatus,
  type ProjectActivationState,
} from '@lia/contracts';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDateTime } from '../../lib/format.js';
import { Badge, StatusBadge, type StatusTone } from '../primitives/badge.js';

// Launch gate (setter spec §10.1 checklist, §10.2 dual approval, §10.3 Level B change requests, §13.2 criteria).

const DEFAULT_TZ = 'America/Mexico_City';

export const LAUNCH_STATE_TONE: Record<ProjectActivationState, StatusTone> = {
  setup: 'neutral',
  ready_for_approval: 'info',
  live: 'success',
  paused: 'warning',
};

/** Project activation state: color + icon + text. */
export function LaunchStateBadge({ state, className }: { state: ProjectActivationState; className?: string }) {
  return (
    <StatusBadge tone={LAUNCH_STATE_TONE[state]} className={className}>
      {PROJECT_ACTIVATION_STATE_LABELS[state]}
    </StatusBadge>
  );
}

export const LAUNCH_ITEM_TONE: Record<LaunchItemStatus, StatusTone> = { done: 'success', pending: 'warning', not_applicable: 'neutral' };

export interface LaunchChecklistSection {
  key: string;
  label: string;
  items: LaunchItem[];
}

export interface LaunchChecklistProps {
  sections: readonly LaunchChecklistSection[];
  /** Per-item action (e.g. «Marcar» button for admins). */
  renderItemAction?: (item: LaunchItem) => React.ReactNode;
  timeZone?: string;
  className?: string;
}

/** Go-live checklist grouped by section: auto items (verified by the system) and manual items with evidence. */
export function LaunchChecklist({ sections, renderItemAction, timeZone = DEFAULT_TZ, className }: LaunchChecklistProps) {
  return (
    <div className={cn('grid gap-4', className)}>
      {sections.map((s) => {
        const done = s.items.filter((i) => i.status !== 'pending').length;
        return (
          <section key={s.key} aria-label={s.label} className="grid gap-2">
            <h3 className="flex flex-wrap items-center gap-2 text-body font-semibold text-text-primary">
              {s.label}
              <span className="ef-tabular text-meta font-normal text-text-secondary">
                {done} de {s.items.length}
              </span>
            </h3>
            <ul className="grid divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-default">
              {s.items.map((i) => (
                <li key={i.key} data-item={i.key} className="grid gap-1.5 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-body font-medium text-text-primary">{i.label}</span>
                    <StatusBadge tone={LAUNCH_ITEM_TONE[i.status]}>{LAUNCH_ITEM_STATUS_LABELS[i.status]}</StatusBadge>
                    <Badge tone="outline">{i.kind === 'auto' ? 'Automático' : 'Manual'}</Badge>
                  </div>
                  {i.detail ? <p className="text-meta text-text-primary">{i.detail}</p> : null}
                  {i.hint && i.status === 'pending' ? <p className="text-meta text-text-secondary">{i.hint}</p> : null}
                  {i.note ? (
                    <p className="text-meta text-text-primary">
                      <span className="font-medium">{i.status === 'not_applicable' ? 'Justificación: ' : 'Evidencia: '}</span>
                      {i.note}
                    </p>
                  ) : null}
                  {i.link ? (
                    <p className="text-meta">
                      <a href={i.link} target="_blank" rel="noreferrer noopener" className="break-all text-brand-primary underline-offset-2 hover:underline">
                        {i.link}
                      </a>
                    </p>
                  ) : null}
                  {i.updatedBy ? (
                    <p className="text-meta text-text-secondary">
                      por {i.updatedBy.name}
                      {i.updatedAt ? ` · ${formatDateTime(i.updatedAt, timeZone)}` : ''}
                    </p>
                  ) : null}
                  {renderItemAction ? <div className="flex flex-wrap gap-2">{renderItemAction(i)}</div> : null}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export interface LaunchApprovalListProps {
  approvals: readonly LaunchApproval[];
  timeZone?: string;
  className?: string;
}

/** Approval history (§10.2): who approved which role, when, on which configuration version. */
export function LaunchApprovalList({ approvals, timeZone = DEFAULT_TZ, className }: LaunchApprovalListProps) {
  if (!approvals.length) return <p className={cn('text-meta text-text-secondary', className)}>Aún no hay aprobaciones registradas.</p>;
  return (
    <ul aria-label="Historial de aprobaciones" className={cn('grid divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-default', className)}>
      {approvals.map((a) => (
        <li key={a.id} className="grid gap-1 px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body font-medium text-text-primary">
              Aprobación {LAUNCH_APPROVER_ROLE_LABELS[a.role].toLowerCase()} · {a.user.name}
            </span>
            <StatusBadge tone={a.valid ? 'success' : 'neutral'}>{a.valid ? 'Vigente' : 'Sin efecto'}</StatusBadge>
            <span className="ef-tabular text-meta text-text-secondary">Ronda {a.round}</span>
          </div>
          <p className="text-meta text-text-secondary">
            {formatDateTime(a.createdAt, timeZone)} · versión de configuración <span className="ef-tabular font-mono">{a.configHash}</span>
          </p>
          {a.plannedActivationAt ? <p className="text-meta text-text-primary">Activación prevista: {formatDateTime(a.plannedActivationAt, timeZone)}</p> : null}
          {a.note ? <p className="text-meta text-text-primary">{a.note}</p> : null}
          {!a.valid && a.invalidReason ? <p className="text-meta text-text-primary">Motivo: {a.invalidReason}</p> : null}
        </li>
      ))}
    </ul>
  );
}

export const CHANGE_REQUEST_TONE: Record<ChangeRequest['state'], StatusTone> = { pending: 'info', applied: 'success', rejected: 'neutral', failed: 'danger' };

export interface ChangeRequestListProps {
  items: readonly ChangeRequest[];
  /** Actions for pending requests (approve / reject). */
  renderActions?: (cr: ChangeRequest) => React.ReactNode;
  timeZone?: string;
  emptyText?: string;
  className?: string;
}

/** Level B change requests (§10.3): pending until the commercial and the technical approver accept them. */
export function ChangeRequestList({ items, renderActions, timeZone = DEFAULT_TZ, emptyText = 'No hay cambios de nivel B pendientes ni recientes.', className }: ChangeRequestListProps) {
  if (!items.length) return <p className={cn('text-meta text-text-secondary', className)}>{emptyText}</p>;
  const mark = (label: string, m: ChangeRequest['commercial']) => (
    <span>
      {label}: {m ? `${m.user.name} · ${formatDateTime(m.at, timeZone)}` : 'pendiente'}
    </span>
  );
  return (
    <ul aria-label="Cambios de nivel B" className={cn('grid divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-default', className)}>
      {items.map((c) => (
        <li key={c.id} className="grid gap-1 px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body font-medium text-text-primary">{LEVEL_B_CHANGE_KIND_LABELS[c.kind]}</span>
            <StatusBadge tone={CHANGE_REQUEST_TONE[c.state]}>{CHANGE_REQUEST_STATE_LABELS[c.state]}</StatusBadge>
          </div>
          <p className="text-meta text-text-primary">{c.summary}</p>
          <p className="text-meta text-text-secondary">
            Solicitado por {c.requestedBy.name} · {formatDateTime(c.createdAt, timeZone)}
          </p>
          <p className="flex flex-wrap gap-x-4 text-meta text-text-secondary">
            {mark('Comercial', c.commercial)}
            {mark('Técnica', c.technical)}
          </p>
          {c.decisionNote ? <p className="text-meta text-text-primary">Nota: {c.decisionNote}</p> : null}
          {c.applyError ? <p className="text-meta text-danger">No se aplicó: {c.applyError}</p> : null}
          {c.state === 'pending' && renderActions ? <div className="flex flex-wrap gap-2">{renderActions(c)}</div> : null}
        </li>
      ))}
    </ul>
  );
}
